import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spinner } from 'reactstrap';
import { BiCheckCircle, BiTime, BiErrorCircle, BiLockAlt, BiCloud } from 'react-icons/bi';
import { toast } from 'react-toastify';
import * as signalR from '@microsoft/signalr';
import useTierFeatures from '../../hooks/useTierFeatures';
import { fetchRecordStatus, syncInvoice, syncExpense, syncCustomer } from '../../api/integrationsApi';

const HUB_URL = `${import.meta.env.VITE_API_URL}/hub/liverefresh`;

// Per-record accounting sync status + manual Send/Retry. type: 'Invoice' |
// 'Expense' | 'Customer'. Renders nothing for orgs without the feature (Solo).
const META = {
    synced: { label: 'Synced', color: '#10b981', Icon: BiCheckCircle },
    pending: { label: 'Sending…', color: '#d97706', Icon: BiTime },
    failed: { label: 'Failed', color: '#ef4444', Icon: BiErrorCircle },
    locked: { label: 'Locked (paid)', color: '#64748b', Icon: BiLockAlt },
    none: { label: 'Not synced', color: '#64748b', Icon: BiCloud },
};

export default function AccountingSyncBadge({ type, id, onStatusChange }) {
    const { accountingEnabled } = useTierFeatures();
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);
    // Held in a ref so a non-memoised parent callback doesn't churn `load`'s
    // identity (which would otherwise reconnect the SignalR hub each render).
    const onStatusChangeRef = useRef(onStatusChange);
    onStatusChangeRef.current = onStatusChange;

    const load = useCallback(async () => {
        if (!id) return;
        try {
            const s = await fetchRecordStatus(type, id);
            setStatus(s);
            onStatusChangeRef.current?.(s);
        } catch { /* ignore */ }
    }, [type, id]);

    useEffect(() => { if (accountingEnabled) load(); }, [accountingEnabled, load]);

    // Live-update the badge when the worker finishes the push (Sending → Synced)
    // or a payment webhook locks the record.
    useEffect(() => {
        if (!accountingEnabled || !id) return undefined;
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, { accessTokenFactory: () => window.Clerk.session.getToken({ template: 'marginpal-api' }) })
            .withAutomaticReconnect()
            .build();
        connection.on('UpdateAccountingStatus', () => load());
        connection.start().catch(() => {});
        return () => { connection.stop(); };
    }, [accountingEnabled, id, load]);

    if (!accountingEnabled) return null;

    const key = status?.status ?? 'none';
    const meta = META[key] ?? META.none;
    const { Icon } = meta;

    const send = async () => {
        setBusy(true);
        try {
            const fn = type === 'Expense' ? syncExpense : type === 'Customer' ? syncCustomer : syncInvoice;
            await fn(id);
            await load();
            toast.success('Sent to your accounting package.');
        } catch (err) {
            toast.error(err?.message ?? 'Could not send.');
        } finally { setBusy(false); }
    };

    return (
        <div className="d-flex align-items-center justify-content-between border rounded-3 px-3 py-2">
            <span className="d-flex align-items-center gap-2 fw-semibold small" style={{ color: meta.color }}>
                <Icon size={16} /> {meta.label}
            </span>
            {key !== 'locked' && (
                <Button type="button" size="sm" color="primary" outline disabled={busy} onClick={send}>
                    {busy ? <Spinner size="sm" /> : (key === 'failed' ? 'Retry' : 'Send')}
                </Button>
            )}
        </div>
    );
}
